import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { QRCodeSVG } from "qrcode.react";
import { Printer, Loader2, ArrowLeft, Save, Calendar, Clock, Link2, Share2, Check, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/lib/toastBanner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(d) {
  if (!d) return "";
  try {
    const [y, m, day] = d.split("-").map((s) => parseInt(s, 10));
    if (!y || !m || !day) return d;
    return new Date(y, m - 1, day).toLocaleDateString(undefined, { weekday: "long", year: "numeric", month: "long", day: "numeric" });
  } catch { return d; }
}

function formatTime(t) {
  if (!t) return "";
  try {
    const [h, m] = t.split(":");
    const hh = parseInt(h, 10);
    if (isNaN(hh)) return t;
    const period = hh >= 12 ? "PM" : "AM";
    const display = ((hh + 11) % 12) + 1;
    return `${display}:${m || "00"} ${period}`;
  } catch { return t; }
}

function looksLikeUrl(value) {
  const v = (value || "").trim();
  if (!v) return false;
  try {
    const u = new URL(v);
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
}

export default function EventSurveyPrint() {
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [surveyUrl, setSurveyUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/events/${id}`, { withCredentials: true });
      setEvent(data?.event || null);
      setSurveyUrl(data?.event?.survey_url || "");
    } catch {
      toast.error("Failed to load event");
    } finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // Toggle a body class so print rules can hide AdminLayout's chrome only
  // while this page is mounted.
  useEffect(() => {
    document.body.classList.add("event-print-active");
    return () => document.body.classList.remove("event-print-active");
  }, []);

  const saveSurveyUrl = async () => {
    const trimmed = (surveyUrl || "").trim();
    if (trimmed && !looksLikeUrl(trimmed)) {
      toast.error("Enter a full URL starting with http:// or https://");
      return false;
    }
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/events/${id}`, { survey_url: trimmed }, { withCredentials: true });
      setEvent(data);
      setSurveyUrl(data?.survey_url || "");
      toast.success("Survey link saved");
      return true;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
      return false;
    } finally { setSaving(false); }
  };

  const handlePrint = async () => {
    if ((surveyUrl || "").trim() !== (event?.survey_url || "")) {
      const ok = await saveSurveyUrl();
      if (!ok) return;
    }
    window.print();
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[#6E7787]" /></div>;
  }
  if (!event) {
    return <div className="py-12 text-center text-sm text-[#6E7787]">Event not found.</div>;
  }

  const trimmedUrl = (surveyUrl || "").trim();
  const isValid = looksLikeUrl(trimmedUrl);
  const persisted = (event.survey_url || "").trim();
  const persistedValid = looksLikeUrl(persisted);
  const shareUrl = `${window.location.origin}/events/${id}/survey`;
  const handleCopy = async () => {
    try {
      if (!persistedValid) {
        toast.error("Save a valid survey URL first");
        return;
      }
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Share link copied");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Copy failed");
    }
  };

  return (
    <div className="min-h-screen bg-white text-slate-900 event-print-root">
      {/* Toolbar — hidden on print */}
      <div className="print:hidden border-b border-slate-200 bg-white sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3 flex-wrap">
          <button
            onClick={() => window.history.back()}
            className="inline-flex items-center gap-1.5 text-sm font-body text-slate-600 hover:text-slate-900"
            data-testid="event-survey-print-back"
          >
            <ArrowLeft className="h-4 w-4" /> Back
          </button>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              onClick={saveSurveyUrl}
              disabled={saving}
              className="gap-1.5 border-slate-300 text-slate-700 hover:bg-slate-100"
              data-testid="event-survey-print-save"
            >
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Save className="h-3.5 w-3.5" /> Save</>}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleCopy}
              disabled={!persistedValid}
              className="gap-1.5 border-slate-300 text-slate-700 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="event-survey-share-copy"
              title={persistedValid ? "Copy a public link that displays this QR page" : "Save a survey URL first"}
            >
              {copied ? <><Check className="h-3.5 w-3.5 text-emerald-600" /> Copied</> : <><Share2 className="h-3.5 w-3.5" /> Share link</>}
            </Button>
            <Button
              variant="outline"
              size="sm"
              asChild
              disabled={!persistedValid}
              className="gap-1.5 border-slate-300 text-slate-700 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="event-survey-share-open"
            >
              <a href={persistedValid ? shareUrl : "#"} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="h-3.5 w-3.5" /> Open
              </a>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handlePrint}
              disabled={!isValid}
              className="gap-1.5 border-slate-300 text-slate-700 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="event-survey-print-trigger"
            >
              <Printer className="h-3.5 w-3.5" /> Print
            </Button>
          </div>
        </div>
      </div>

      {/* Editable URL field — hidden on print */}
      <div className="print:hidden max-w-4xl mx-auto px-4 sm:px-6 pt-6">
        <p className="text-xs uppercase tracking-wider text-slate-500 font-body mb-2">Post-event survey URL</p>
        <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-3" data-testid="event-survey-print-editor">
          <div className="flex items-center gap-2">
            <Link2 className="h-4 w-4 text-slate-400 shrink-0" />
            <Input
              type="url"
              value={surveyUrl}
              onChange={(e) => setSurveyUrl(e.target.value)}
              placeholder="https://forms.example.com/your-post-event-survey"
              className="font-body"
              data-testid="event-survey-url-input"
              autoComplete="off"
              spellCheck={false}
            />
          </div>
          {trimmedUrl && !isValid ? (
            <p className="text-xs text-red-600 font-body">Enter a full URL starting with <code>http://</code> or <code>https://</code>.</p>
          ) : (
            <p className="text-xs text-slate-500 font-body">
              Paste the link to your post-event survey (Google Forms, Typeform, Microsoft Forms, etc.). The QR code below regenerates as you type. Save before printing to keep your change.
            </p>
          )}
          {persistedValid ? (
            <div className="pt-2 border-t border-slate-100" data-testid="event-survey-share-row">
              <p className="text-xs uppercase tracking-wider text-slate-500 font-body mb-1.5">Public share link (TV / projector)</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-xs font-mono text-slate-700 bg-slate-50 border border-slate-200 rounded px-2.5 py-1.5 truncate" data-testid="event-survey-share-url">{shareUrl}</code>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleCopy}
                  className="gap-1.5 border-slate-300 shrink-0"
                  data-testid="event-survey-share-copy-inline"
                >
                  {copied ? <><Check className="h-3.5 w-3.5 text-emerald-600" /> Copied</> : <>Copy</>}
                </Button>
              </div>
              <p className="text-[11px] text-slate-500 font-body mt-1.5">Anyone with this link sees a full-screen QR page — no login required. Perfect for casting on a TV at the venue.</p>
            </div>
          ) : null}
        </div>
      </div>

      {/* Print sheet */}
      <div className="event-print-sheet max-w-4xl mx-auto px-6 sm:px-10 py-10 sm:py-12 mt-6 sm:mt-8 print:mt-0 print:px-12 print:py-10">
        <div className="text-center">
          <p className="text-xs uppercase tracking-[0.25em] text-slate-500 font-body" data-testid="event-survey-print-eyebrow">Thank you for attending</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold mt-3 text-slate-900 leading-tight" data-testid="event-survey-print-name">{event.name}</h1>
          <div className="mt-4 inline-flex flex-wrap items-center justify-center gap-x-6 gap-y-1.5 text-base text-slate-600 font-body">
            <span className="inline-flex items-center gap-1.5"><Calendar className="h-4 w-4" /> {formatDate(event.date)}</span>
            <span className="inline-flex items-center gap-1.5"><Clock className="h-4 w-4" /> {formatTime(event.start_time)}</span>
          </div>
        </div>

        {/* QR code block */}
        <div className="mt-12 flex flex-col items-center gap-4">
          {isValid ? (
            <>
              <div className="p-4 bg-white border-2 border-slate-200 rounded-xl" data-testid="event-survey-print-qr">
                <QRCodeSVG
                  value={trimmedUrl}
                  size={300}
                  level="M"
                  includeMargin={false}
                  bgColor="#FFFFFF"
                  fgColor="#0A0C10"
                />
              </div>
              <p className="text-xs uppercase tracking-[0.25em] text-slate-500 font-body">Scan to open the survey</p>
              <p className="text-xs text-slate-500 font-body text-center max-w-sm">
                Open your phone's camera and point it at this QR code, then tap the link that appears.
              </p>
            </>
          ) : (
            <div className="rounded-xl border-2 border-dashed border-slate-300 px-10 py-16 text-center print:hidden" data-testid="event-survey-print-placeholder">
              <p className="font-heading text-lg font-semibold text-slate-700">Add a survey URL to generate the QR code</p>
              <p className="text-sm font-body text-slate-500 mt-1">
                The print button enables once a valid URL is entered above.
              </p>
            </div>
          )}
        </div>

        {/* URL fallback line on the printed sheet (small) */}
        {isValid ? (
          <p className="mt-8 text-center text-[11px] font-body text-slate-400 break-all" data-testid="event-survey-print-url">
            Or visit: {trimmedUrl}
          </p>
        ) : null}

        {/* Footnote if user has a saved URL but is previewing a different one */}
        {persistedValid && trimmedUrl !== persisted ? (
          <p className="print:hidden mt-6 text-center text-[11px] font-body text-amber-600">
            Unsaved change — printing will save the new URL first.
          </p>
        ) : null}
      </div>

      {/* Print styles — same scoping as EventPrint */}
      <style>{`
        @media print {
          @page { size: letter; margin: 0.4in; }
          body, html { background: #ffffff !important; }
          .event-print-root { background: #ffffff !important; }
          .event-print-sheet { box-shadow: none !important; max-width: 100% !important; margin: 0 !important; padding: 0 !important; }
        }
        body.event-print-active * { visibility: visible; }
        @media print {
          body.event-print-active aside,
          body.event-print-active header,
          body.event-print-active nav,
          body.event-print-active footer,
          body.event-print-active .print\\:hidden { display: none !important; }
        }
      `}</style>
    </div>
  );
}
